import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./RetailsManufacturing.css";
import Testimonial from "../../../Testimonial/Testimonial";

const RetailsManufacturing = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1;

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  const [activeSolution, setActiveSolution] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const interval = setInterval(() => {
      setActiveSolution((prev) => (prev + 1) % 4);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const solutionsData = [
    {
      title: "Inventory Management",
      description:
        "Optimize stock levels, reduce waste, and automate replenishment with AI-powered inventory systems.",
      features: [
        "Real-time stock tracking",
        "Demand forecasting",
        "Automated reordering",
        "Multi-location management",
      ],
      icon: "📦",
    },
    {
      title: "Supply Chain Visibility",
      description:
        "End-to-end visibility across your entire supply chain with real-time tracking and analytics.",
      features: [
        "Supplier management",
        "Logistics tracking",
        "Quality control",
        "Compliance monitoring",
      ],
      icon: "🔗",
    },
    {
      title: "Smart Manufacturing",
      description:
        "Leverage IoT and AI to optimize production processes and improve operational efficiency.",
      features: [
        "Predictive maintenance",
        "Production monitoring",
        "Quality assurance",
        "Energy management",
      ],
      icon: "🏭",
    },
    {
      title: "Retail Analytics",
      description:
        "Gain deep insights into customer behavior, sales patterns, and market trends.",
      features: [
        "Customer segmentation",
        "Sales forecasting",
        "Price optimization",
        "Performance dashboards",
      ],
      icon: "📈",
    },
  ];

const manufacturingFeatures = [
    {
      icon: "🤖",
      title: "Industrial IoT",
      description: "Connect machines and sensors for real-time monitoring",
    },
    {
      icon: "📊",
      title: "Production Analytics",
      description: "Optimize manufacturing processes with data insights",
    },
    {
      icon: "⚙️",
      title: "Quality Control",
      description: "Automated inspection and defect detection systems",
    },
    {
      icon: "🔧",
      title: "Maintenance Planning",
      description: "Predictive maintenance to reduce downtime",
    },
    {
      icon: "🏭",
      title: "Digital Twin Technology",
      description: "Virtual replicas of physical systems for simulation and testing",
    },
    {
      icon: "📦",
      title: "Smart Inventory Management",
      description: "AI-powered inventory optimization and automated replenishment",
    },
    {
      icon: "🚚",
      title: "Supply Chain Visibility",
      description: "End-to-end tracking of materials and components across the supply chain",
    },
    {
      icon: "🔍",
      title: "Predictive Quality Analytics",
      description: "AI algorithms that predict quality issues before they occur",
    },
    {
      icon: "⚡",
      title: "Energy Management Systems",
      description: "Monitor and optimize energy consumption across manufacturing facilities",
    },
    {
      icon: "👁️",
      title: "Computer Vision Inspection",
      description: "Advanced visual inspection systems for defect detection and quality assurance",
    },
    {
      icon: "🤝",
      title: "Collaborative Robotics",
      description: "Safe human-robot collaboration for enhanced productivity and ergonomics",
    },
    {
      icon: "📱",
      title: "Operator Assistance Systems",
      description: "AR-guided assembly and maintenance procedures for shop floor workers",
    },
    {
      icon: "🧠",
      title: "AI-Powered Process Optimization",
      description: "Machine learning algorithms that continuously improve manufacturing processes",
    },
    {
      icon: "🔗",
      title: "Blockchain Traceability",
      description: "Immutable record-keeping for product provenance and supply chain transparency",
    },
    {
      icon: "🔄",
      title: "Circular Economy Solutions",
      description: "Systems for tracking and optimizing material reuse and recycling",
    },
    {
      icon: "🌡️",
      title: "Environmental Monitoring",
      description: "Real-time tracking of temperature, humidity, and other critical environmental factors",
    },
    {
      icon: "📈",
      title: "OEE Monitoring",
      description: "Overall Equipment Effectiveness tracking with real-time dashboards and alerts",
    },
    {
      icon: "🛡️",
      title: "Cybersecurity for OT",
      description: "Industrial cybersecurity solutions protecting operational technology systems",
    },
    {
      icon: "🧪",
      title: "Digital Work Instructions",
      description: "Interactive, paperless work instructions for assembly and maintenance tasks",
    },
    {
      icon: "🚀",
      title: "Additive Manufacturing Integration",
      description: "Seamless integration of 3D printing into traditional manufacturing workflows",
    },
  ];

const retailFeatures = [
    {
      icon: "🛒",
      title: "Omnichannel Retail",
      description: "Seamless shopping experience across all channels",
    },
    {
      icon: "👥",
      title: "Customer Engagement",
      description: "Personalized marketing and loyalty programs",
    },
    {
      icon: "💰",
      title: "POS Systems",
      description: "Advanced point-of-sale with integrated inventory",
    },
    {
      icon: "🚚",
      title: "Last-Mile Delivery",
      description: "Optimized delivery routes and tracking",
    },
    {
      icon: "📱",
      title: "Mobile Commerce",
      description: "Optimized shopping experiences for mobile devices",
    },
    {
      icon: "🤖",
      title: "AI-Powered Recommendations",
      description: "Personalized product suggestions based on customer behavior",
    },
    {
      icon: "📊",
      title: "Retail Analytics",
      description: "Data-driven insights for inventory, sales, and customer trends",
    },
    {
      icon: "🏷️",
      title: "Dynamic Pricing",
      description: "Real-time price optimization based on demand and competition",
    },
    {
      icon: "📦",
      title: "Inventory Optimization",
      description: "AI-driven stock management to reduce overstock and out-of-stocks",
    },
    {
      icon: "👗",
      title: "Virtual Try-On",
      description: "AR technology allowing customers to visualize products",
    },
    {
      icon: "🔄",
      title: "Returns Management",
      description: "Streamlined return processes with automated workflows",
    },
    {
      icon: "🗣️",
      title: "Voice Commerce",
      description: "Voice-activated shopping and customer service",
    },
    {
      icon: "🎯",
      title: "Personalized Marketing",
      description: "Targeted campaigns based on individual customer preferences",
    },
    {
      icon: "📝",
      title: "Digital Shelf Analytics",
      description: "Monitor product visibility and performance across online platforms",
    },
    {
      icon: "🤝",
      title: "Supplier Collaboration",
      description: "Real-time communication and data sharing with suppliers",
    },
    {
      icon: "🔐",
      title: "Fraud Prevention",
      description: "Advanced security systems to detect and prevent retail fraud",
    },
    {
      icon: "🌐",
      title: "Global E-commerce",
      description: "Multi-currency and multi-language online store capabilities",
    },
    {
      icon: "📅",
      title: "Appointment Scheduling",
      description: "Book in-store services and consultations online",
    },
    {
      icon: "🧠",
      title: "Predictive Analytics",
      description: "Forecast demand and buying trends with machine learning",
    },
    {
      icon: "🛍️",
      title: "Social Commerce",
      description: "Shoppable posts and social media integration",
    },
  ];

  return (
    <div>
      {/* ✅ Helmet for SEO */}
      <Helmet>
        <title>Retail & Manufacturing Solutions | Tools & Tech Stack</title>
        <meta
          name="description"
          content="Explore advanced retail and manufacturing solutions with our wide range of tools and tech stack including Angular, React Native, AI, Blockchain, IoT, Salesforce, and more."
        />
        <meta
          name="keywords"
          content="Retail solutions, Manufacturing technology, Tech stack, Angular, React Native, Node.js, Artificial Intelligence, Blockchain, IoT, Laravel, PHP, SQL Server, MongoDB, MySQL, PostgreSQL, Data Science, Cloud Computing, Flutter, Ionic, Solidity, Hyperledger, Virtual Reality, Augmented Reality"
        />
      </Helmet>
      <div className="mk-rm-wrapper">
        {/* Hero Section */}
        <section className={`mk-rm-hero ${isVisible ? "mk-rm-visible" : ""}`}>
          <div className="mk-rm-hero-content">
            <h1 className="mk-rm-hero-title">
              Transforming{" "}
              <span className="mk-rm-text-gradient">
                Retail & Manufacturing
              </span>{" "}
              with Technology
            </h1>
            <p className="mk-rm-hero-description">
              MK eTech provides cutting-edge software solutions that empower
              retailers and manufacturers to optimize operations, reduce costs,
              and enhance customer experiences.
            </p>
            <div className="mk-rm-hero-buttons">
              <a href="/contact-us">
                <button className="mk-rm-primary-btn">Contact Us</button>
              </a>
              <a
                href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20exploring%20your%20solutions%20for%20Retail%20and%20Manufacturing.%20Please%20schedule%20a%20demo%20to%20discuss%20how%20you%20can%20help%20transform%20my%20business."
                target="_blank"
                rel="noreferrer"
              >
                <button className="mk-rm-secondary-btn">Request Demo</button>
              </a>
            </div>
          </div>
          <div className="mk-rm-hero-visual">
            <div className="mk-rm-industry-animation">
              <div className="mk-rm-factory"></div>
              <div className="mk-rm-conveyor"></div>
              <div className="mk-rm-products">
                <div className="mk-rm-product mk-rm-product-1"></div>
                <div className="mk-rm-product mk-rm-product-2"></div>
                <div className="mk-rm-product mk-rm-product-3"></div>
              </div>
              <div className="mk-rm-retail-store"></div>
            </div>
          </div>
        </section>

        {/* Solutions Section */}
        <section className="mk-rm-solutions">
          <h2 className="mk-rm-section-title">Integrated Solutions</h2>
          <p className="mk-rm-section-subtitle">
            End-to-end software solutions for retail and manufacturing
            industries
          </p>

          <div className="mk-rm-solutions-container">
            <div className="mk-rm-solutions-tabs">
              {solutionsData.map((solution, index) => (
                <button
                  key={index}
                  className={`mk-rm-solution-tab ${
                    activeSolution === index ? "mk-rm-tab-active" : ""
                  }`}
                  onClick={() => setActiveSolution(index)}
                >
                  <span className="mk-rm-tab-icon">{solution.icon}</span>
                  {solution.title}
                </button>
              ))}
            </div>

            <div className="mk-rm-solution-content">
              <div className="mk-rm-solution-visual">
                <div
                  className={`mk-rm-solution-animation mk-rm-solution-${activeSolution}`}
                >
                  {/* Animation will change based on activeSolution */}
                </div>
              </div>
              <div className="mk-rm-solution-info">
                <h3>{solutionsData[activeSolution].title}</h3>
                <p>{solutionsData[activeSolution].description}</p>
                <ul className="mk-rm-solution-features">
                  {solutionsData[activeSolution].features.map(
                    (feature, index) => (
                      <li key={index}>{feature}</li>
                    )
                  )}
                </ul>
                {/* <button className="mk-rm-learn-more">Learn More</button> */}
              </div>
            </div>
          </div>
        </section>

        {/* Manufacturing Features */}
        <section className="mk-rm-manufacturing">
          <div className="mk-rm-section-header">
            <h2 className="mk-rm-section-title">Manufacturing Solutions</h2>
            <p className="mk-rm-section-subtitle">
              Optimize production processes with Industry 4.0 technologies
            </p>
          </div>

          <div className="mk-rm-features-grid">
            {manufacturingFeatures.map((feature, index) => (
              <div key={index} className="mk-rm-feature-card">
                <div className="mk-rm-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Retail Features */}
        <section className="mk-rm-retail">
          <div className="mk-rm-section-header">
            <h2 className="mk-rm-section-title">Retail Solutions</h2>
            <p className="mk-rm-section-subtitle">
              Transform customer experiences and streamline operations
            </p>
          </div>

          <div className="mk-rm-features-grid">
            {retailFeatures.map((feature, index) => (
              <div key={index} className="mk-rm-feature-card">
                <div className="mk-rm-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
       <section className="mk-rm-benefits">
  <h2 className="mk-rm-section-title">Key Benefits</h2>

  <div className="mk-rm-benefits-container">
    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">💰</div>
      <h3>Cost Reduction</h3>
      <p>
        Reduce operational costs by up to 30% with optimized processes
        and automation
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">⚡</div>
      <h3>Efficiency Boost</h3>
      <p>
        Increase productivity by 40% with streamlined workflows and
        real-time data
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">📈</div>
      <h3>Revenue Growth</h3>
      <p>
        Drive sales growth with data-driven insights and personalized
        customer experiences
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">🌱</div>
      <h3>Sustainability</h3>
      <p>
        Reduce waste and energy consumption with smart resource
        management
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">🔄</div>
      <h3>Supply Chain Resilience</h3>
      <p>
        Build robust supply chains with real-time visibility and risk mitigation capabilities
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">🎯</div>
      <h3>Customer Satisfaction</h3>
      <p>
        Improve customer experience with personalized service and faster response times
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">📊</div>
      <h3>Data-Driven Decisions</h3>
      <p>
        Make informed decisions with real-time analytics and predictive insights
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">🔒</div>
      <h3>Enhanced Security</h3>
      <p>
        Protect sensitive data with advanced cybersecurity measures and compliance protocols
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">🤖</div>
      <h3>Automation Advantage</h3>
      <p>
        Automate repetitive tasks to free up staff for higher-value activities
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">🌍</div>
      <h3>Global Scalability</h3>
      <p>
        Expand your operations globally with cloud-based solutions that scale effortlessly
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">⏱️</div>
      <h3>Faster Time-to-Market</h3>
      <p>
        Accelerate product launches with streamlined development and production processes
      </p>
    </div>

    <div className="mk-rm-benefit">
      <div className="mk-rm-benefit-icon">👥</div>
      <h3>Team Collaboration</h3>
      <p>
        Enhance teamwork with integrated communication and project management tools
      </p>
    </div>
  </div>
</section>

        {/* CTA Section */}
        <section className="mk-rm-cta">
          <div className="mk-rm-cta-content">
            <h2>Ready to Transform Your Business?</h2>
            <p>
              Join industry leaders who trust MK eTech with their digital
              transformation journey.
            </p>
            <div className="mk-rm-cta-buttons">
              <a href="/contact-us"><button className="mk-rm-primary-btn mk-rm-cta-btn">
                Contact Us
              </button></a>
          <a
  href="https://wa.me/919155599968?text=Hi!%20I%20am%20looking%20for%20Retail%20and%20Manufacturing%20solutions%20to%20improve%20operations%20and%20efficiency.%20Please%20connect%20me%20with%20your%20sales%20team%20for%20more%20details."
  target="_blank"
  rel="noreferrer"
>
  <button className="mk-rm-secondary-btn mk-rm-cta-btn">
    Contact Sales
  </button>
</a>

            </div>
          </div>
        </section>
      </div>
      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailsManufacturing;
